import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  interveneChannelListAtom,
  interveneFilterAtom,
  interveneMessageListAtom,
  interveneUserDetailAtom,
} from '../state/interveneAtoms';
import { userInformationAtom } from '../../../Services/_states';
import { interveneService } from '../services/interveneService';
import { getOrganisationId } from '../utils/organisationSelectUtils';
import useInterveneSuperAdmin from './useInterveneSuperAdmin';
import {
  applyInterveneOrganisationScope,
  lockInterveneOrganisationForUser,
} from '../utils/interveneOrganisationScope';

function formatChannels(source = []) {
  return source
    .map((obj) => {
      if (!obj) return null;

      const phoneNumber =
        obj.channel_number ||
        obj.wa_phone_number ||
        obj.bot_number ||
        obj.phone_number ||
        '';

      return {
        ...obj,
        channel_number: phoneNumber,
        wa_phone_number: obj.wa_phone_number || phoneNumber,
      };
    })
    .filter(Boolean);
}

export default function useChannels() {
  const userInfo = useRecoilValue(userInformationAtom);
  const isSuperAdmin = useInterveneSuperAdmin();

  const [channels, setChannels] = useRecoilState(interveneChannelListAtom);
  const [filters, setFilters] = useRecoilState(interveneFilterAtom);

  const setUserDetail = useSetRecoilState(interveneUserDetailAtom);
  const setMessageList = useSetRecoilState(interveneMessageListAtom);

  const [isLoadingChannels, setIsLoadingChannels] = useState(false);

  const fetchedOrganisationRef = useRef('');

  useEffect(() => {
    setFilters((prev) => {
      const next = applyInterveneOrganisationScope(prev, userInfo);
      if (
        next.organisationId === prev.organisationId
        && next.organisationObj === prev.organisationObj
      ) {
        return prev;
      }
      return next;
    });
  }, [userInfo, setFilters]);

  const resetConversationState = useCallback(
    (botNumber = '') => {
      setUserDetail({});

      setMessageList({
        messages: [],
        total_count: 0,
        filters: {
          page: 1,
          phone_number: '',
          botNumber,
        },
      });
    },
    [setMessageList, setUserDetail]
  );

  const fetchChannels = useCallback(
    async (organisationId, { preserveSelection = true } = {}) => {
      if (!organisationId) {
        setChannels([]);
        fetchedOrganisationRef.current = '';
        return;
      }

      const organisationKey = String(organisationId);

      setIsLoadingChannels(true);

      try {
        const res = await interveneService.getAllChannels(organisationId);

        if (res?.status !== 200) {
          setChannels([]);
          return;
        }

        let source = [];

        if (Array.isArray(res?.data)) {
          source = res.data;
        } else if (Array.isArray(res?.data?.data)) {
          source = res.data.data;
        } else if (
          res?.data &&
          typeof res.data === 'object'
        ) {
          source = Object.values(res.data);
        }

        const formattedChannels = formatChannels(source);

        setChannels(formattedChannels);

        fetchedOrganisationRef.current = organisationKey;

        const persistedBotNumber = String(
          filters.botNumber || ''
        ).trim();

        const persistedChannel = filters.channelObj;

        let selectedChannel = null;

        if (preserveSelection && persistedBotNumber) {
          selectedChannel = formattedChannels.find(
            (channel) =>
              String(channel.channel_number || '').trim() ===
              persistedBotNumber
          );
        }

        if (
          !selectedChannel &&
          preserveSelection &&
          persistedChannel
        ) {
          selectedChannel = formattedChannels.find((channel) => {
            if (
              channel.app_id != null &&
              persistedChannel.app_id != null
            ) {
              return (
                String(channel.app_id) ===
                String(persistedChannel.app_id)
              );
            }

            if (
              channel.mongo_id != null &&
              persistedChannel.mongo_id != null
            ) {
              return (
                String(channel.mongo_id) ===
                String(persistedChannel.mongo_id)
              );
            }

            return false;
          });
        }

        if (selectedChannel) {
          const selectedNumber =
            selectedChannel.channel_number || '';

          resetConversationState(selectedNumber);

          setFilters((prev) => lockInterveneOrganisationForUser({
            ...prev,
            organisationId:
              prev.organisationId || organisationKey,
            channelObj: selectedChannel,
            botNumber: selectedNumber,
          }, userInfo));

          return;
        }

        if (
          !persistedBotNumber &&
          formattedChannels.length > 0
        ) {
          const firstChannel = formattedChannels[0];

          const firstNumber =
            firstChannel.channel_number || '';

          resetConversationState(firstNumber);

          setFilters((prev) => lockInterveneOrganisationForUser({
            ...prev,
            organisationId:
              prev.organisationId || organisationKey,
            channelObj: firstChannel,
            botNumber: firstNumber,
            page: 1,
            search: '',
          }, userInfo));
        }
      } catch (err) {
        console.error('Failed to fetch channels', err);
        setChannels([]);
      } finally {
        setIsLoadingChannels(false);
      }
    },
    [
      filters.botNumber,
      filters.channelObj,
      resetConversationState,
      setChannels,
      setFilters,
      userInfo,
    ]
  );

  useEffect(() => {
    const organisationId = isSuperAdmin
      ? filters.organisationId
      : userInfo?.profile?.organisation?.organisation_id;

    if (!organisationId) {
      return;
    }

    const organisationKey = String(organisationId);

    if (
      channels.length > 0 &&
      fetchedOrganisationRef.current === organisationKey
    ) {
      return;
    }

    fetchChannels(organisationId, {
      preserveSelection: true,
    });
  }, [
    isSuperAdmin,
    filters.organisationId,
    userInfo,
    channels.length,
    fetchChannels,
  ]);

  const handleOrganisationSelect = useCallback(
    (organisation) => {
      const organisationId = getOrganisationId(organisation);

      setChannels([]);
      fetchedOrganisationRef.current = '';

      resetConversationState('');

      setFilters((prev) => lockInterveneOrganisationForUser({
        ...prev,
        organisationObj: organisation || null,
        organisationId: organisationId
          ? String(organisationId)
          : '',
        channelObj: null,
        botNumber: '',
        page: 1,
        search: '',
      }, userInfo));

      if (organisationId) {
        fetchChannels(organisationId, {
          preserveSelection: false,
        });
      }
    },
    [
      fetchChannels,
      resetConversationState,
      setChannels,
      setFilters,
      userInfo,
    ]
  );

  const handleChannelSelect = useCallback(
    (channelObj) => {
      if (!channelObj) {
        resetConversationState('');

        setFilters((prev) => lockInterveneOrganisationForUser({
          ...prev,
          channelObj: null,
          botNumber: '',
        }, userInfo));

        return;
      }

      const nextBotNumber =
        channelObj.channel_number || '';

      resetConversationState(nextBotNumber);

      setFilters((prev) => lockInterveneOrganisationForUser({
        ...prev,
        channelObj,
        botNumber: nextBotNumber,
        page: 1,
        search: '',
      }, userInfo));
    },
    [resetConversationState, setFilters, userInfo]
  );

  const hasSelectedOrganisation =
    !isSuperAdmin ||
    Boolean(filters.organisationId);

  const hasActiveChannel =
    Boolean(filters.botNumber);

  return {
    isSuperAdmin,
    hasSelectedOrganisation,
    hasActiveChannel,
    activeOrganisation: filters.organisationObj,
    organisationId: filters.organisationId,
    channels,
    isLoadingChannels,
    activeChannel: filters.channelObj,
    handleOrganisationSelect,
    handleChannelSelect,
  };
}